import React from "react";
import { Button, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconFileAnalytics, IconPlus } from "@tabler/icons-react";
import { Navigate } from "react-router-dom";
import { useGuardedNavigate } from "../../../context/NavigationGuardContext";
import { getLastViewedReportId } from "../../../utils/reportsLastViewed";
import MobileReportsList from "../mobile/MobileReportsList";
import { firstReportInOrder } from "./reportCatalog";
import { reportPath, useReportsShell } from "./reportsShellContext";
import styles from "./ReportsShell.module.css";

/** Centered "No reports in your list" state with an Add report button. */
export function ReportsEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <Stack className={styles.emptyState} align="center" justify="center">
      <Stack align="center" gap="sm" className={styles.emptyInner}>
        <ThemeIcon
          size="xl"
          radius="lg"
          variant="light"
          color="teal"
          className={styles.emptyIcon}
          aria-hidden
        >
          <IconFileAnalytics size={22} />
        </ThemeIcon>
        <Text className={styles.emptyTitle}>No reports in your list</Text>
        <Text className={styles.emptyText}>
          Add the reports you use most. You can add the same report more than
          once to keep different views.
        </Text>
        <Button
          color="teal"
          leftSection={<IconPlus size={15} aria-hidden />}
          onClick={onAdd}
        >
          Add report
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * /admin/reports (index): opens the report this user last viewed at this
 * location (localStorage), else the first report in sidebar order; with an
 * empty list it shows the empty state. Nothing is seeded server-side. On a
 * phone it is the full-screen report list instead.
 */
export default function ReportsIndexPage() {
  const {
    loading,
    loadError,
    reports,
    catalog,
    newReportCount,
    userId,
    salonId,
    openAddReport,
    isMobile,
  } = useReportsShell();
  const navigate = useGuardedNavigate();
  if (loading || loadError) return null;
  if (isMobile) {
    return (
      <MobileReportsList
        reports={reports}
        catalog={catalog}
        newCount={newReportCount}
        onSelect={(report) => navigate(reportPath(report.id))}
        onAddReport={openAddReport}
      />
    );
  }
  if (reports.length === 0) return <ReportsEmptyState onAdd={openAddReport} />;

  const lastViewed = getLastViewedReportId(userId, salonId);
  const target =
    reports.find((r) => r.id === lastViewed) ??
    firstReportInOrder(reports, catalog) ??
    reports[0];
  return <Navigate to={reportPath(target.id)} replace />;
}
